import {
  Home,
  Search,
  Scale,
  Building,
  Calendar,
  FileText,
  Users2,
  BookOpen,
  User2,
  Layers,
  type LucideIcon,
} from "lucide-react";

/* ---------------- TYPES ---------------- */

export interface SidebarItem {
  id: string;
  label: string;
  icon: LucideIcon;
  path?: string;
  children?: SidebarItem[];
}

/* ---------------- COMMON ITEMS ---------------- */

const COMMON = {
  dashboard: {
    id: "dashboard",
    label: "Dashboard",
    icon: Home,
    path: "/dashboard",
  },
  search: {
    id: "search",
    label: "Search Session",
    icon: Search,
    path: "/search-session",
  },
  profile: { id: "profile", label: "Profile", icon: User2, path: "/profile" },
  archivalHistory: {
    id: "archival-history",
    label: "Archival History",
    icon: BookOpen,
    path: "/archival-history",
  },
  meetings: {
    id: "meetings",
    label: "Meetings",
    icon: Layers,
    path: "/meetings",
  },
};

/* ---------------- ADMIN ONLY ---------------- */

const ADMIN = {
  courtSettings: {
    id: "court-settings",
    label: "Court Settings",
    icon: Building,
    path: "/court-settings",
    children: [
      { id: "courts", label: "Courts", icon: Building, path: "/courts" },
      {
        id: "stop-reasons",
        label: "Stop Reasons",
        icon: FileText,
        path: "/stop-reasons",
      },
      {
        id: "retention",
        label: "Retention Duration",
        icon: Calendar,
        path: "/retention-duration",
      },
    ],
  },

  users: {
    id: "users",
    label: "Court Officers",
    icon: Users2,
    path: "/internal-users",
  },
  causeList: {
    id: "cause-list",
    label: "Cause List",
    icon: BookOpen,
    path: "/causelist",
  },
  objections: {
    id: "objections",
    label: "Objection Requests",
    icon: Scale,
    path: "/objection-requests",
  },
  archivalRequests: {
    id: "archival-requests",
    label: "Archival Requests",
    icon: BookOpen,
    path: "/archival-requests",
  },
};

/* ---------------- REPORTS (GROUPED) ---------------- */

const REPORTS: SidebarItem = {
  id: "reports",
  label: "Reports",
  icon: Layers,
  children: [
    {
      id: "archival-report",
      label: "Archive Report",
      icon: BookOpen,
      path: "/reports/archival-report",
    },
    {
      id: "streaming",
      label: "Streaming Activity",
      icon: BookOpen,
      path: "/reports/streaming",
    },
    {
      id: "daily-list",
      label: "Daily List Activity",
      icon: BookOpen,
      path: "/reports/daily-list",
    },
    {
      id: "login",
      label: "Login Activity",
      icon: BookOpen,
      path: "/reports/login",
    },
    {
      id: "registration",
      label: "Registration Activity",
      icon: BookOpen,
      path: "/reports/registration",
    },
    {
      id: "viewers",
      label: "Stream Viewers",
      icon: BookOpen,
      path: "/reports/stream-viewer",
    },
    {
      id: "streams",
      label: "Streams",
      icon: BookOpen,
      path: "/reports/streams",
    },
  ],
};

const CONTROLLER = {
  benchManagment: {
    id: "bench-management",
    label: "Bench Management",
    icon: BookOpen,
    path: "/bench-management",
  },
};

/* ---------------- ROLE CONFIG ---------------- */

export const menuConfig: Record<string, SidebarItem[]> = {
  SuperAdmin: [
    COMMON.dashboard,
    COMMON.search,
    ADMIN.causeList,
    ADMIN.objections,
    ADMIN.archivalRequests,
    REPORTS,
    ADMIN.courtSettings,
    ADMIN.users,
    COMMON.archivalHistory,
    COMMON.meetings,
    COMMON.profile,
  ],

  Judge: [
    COMMON.dashboard,
    {
      id: "reader-delegation",
      label: "Reader Delegation",
      icon: BookOpen,
      path: "/judge/reader-delegation",
    },
    COMMON.search,
    COMMON.archivalHistory,
    COMMON.profile,
  ],

  Controller: [
    COMMON.dashboard,
    COMMON.search,
    ADMIN.causeList,
    ADMIN.archivalRequests,
    ADMIN.objections,
    COMMON.archivalHistory,
    CONTROLLER.benchManagment,
    COMMON.profile,
  ],

  Reader: [
    COMMON.dashboard,
    {
      id: "cause-list",
      label: "Cause List",
      icon: BookOpen,
      path: "/judge/causelist",
    },
    COMMON.search,
    COMMON.archivalHistory,
    COMMON.profile,
  ],

  Advocate: [
    COMMON.dashboard,
    COMMON.search,
    {
      id: "objection-form",
      label: "Objection Form",
      icon: Scale,
      path: "/objection-form",
    },
    COMMON.archivalHistory,
    COMMON.profile,
  ],

  Individual: [
    COMMON.dashboard,
    COMMON.search,
    COMMON.archivalHistory,
    COMMON.profile,
  ],
};
